import React, { useState, useEffect, useMemo } from "react";
import { getAPIURL, getVendorID, getFormattedPrice } from "../../services/helper";
import { getCurrentUser } from "../../services/auth";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner, Container, Card, Input, Button, Row, Col, CardBody, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane, Badge,
} from "reactstrap";
import classnames from "classnames";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const SalesReportByItems = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({ summary: {}, details: [] });
  const [activeTab, setActiveTab] = useState("overview");
  const [itemTypeTab, setItemTypeTab] = useState("all");
  const [itemFilter, setItemFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [compareItems, setCompareItems] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSalesReport();
  }, []);

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);
      const { data } = await http.get(`${api_url}/reports/sales/items?${params}`);
      setData(data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
    }
    setLoading(false);
  };

  const details = useMemo(() => {
    let globalItems = [];
    try {
      const user = getCurrentUser();
      const raw = user?.global_settings?.pos_items;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) globalItems = parsed;
      }
    } catch (e) {}
    const globalIds = new Set(globalItems.map((p) => String(p.id)));
    const reportItems = (data.details || []).map((item) => ({
      ...item,
      item_type: globalIds.has(String(item.product_id)) ? "global" : "local",
    }));
    // Add unsold global items
    const soldIds = new Set(reportItems.map((i) => String(i.product_id)));
    globalItems.forEach((p) => {
      if (!soldIds.has(String(p.id))) {
        reportItems.push({
          product_id: p.id,
          product_name: p.name || p.title || `Product #${p.id}`,
          item_type: "global",
          total_sales: 0,
          total_quantity: 0,
          total_amount: 0,
          total_tax: 0,
          total_discount: 0,
        });
      }
    });
    return reportItems;
  }, [data.details]);
  const s = data.summary || {};

  const filteredItems = useMemo(() => {
    let items = details;
    if (itemTypeTab === "local") items = items.filter((i) => i.item_type === "local");
    if (itemTypeTab === "global") items = items.filter((i) => i.item_type === "global");
    if (itemFilter) items = items.filter((i) => i.product_name?.toLowerCase().includes(itemFilter.toLowerCase()));
    return items;
  }, [details, itemTypeTab, itemFilter]);

  const sorted = [...filteredItems].sort((a, b) => (b.total_quantity || 0) - (a.total_quantity || 0));
  const top5 = sorted.slice(0, 5);
  const least5 = sorted.length > 5 ? sorted.slice(-5).reverse() : [];

  const compareChartData = compareItems.length > 0
    ? compareItems.map((id) => {
        const item = details.find((i) => i.product_id === id);
        return item ? { name: item.product_name, Quantity: item.total_quantity || 0, Revenue: parseFloat(item.total_amount || 0) } : null;
      }).filter(Boolean)
    : [];

  const toggleCompare = (id) => {
    setCompareItems((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const columns = [
    {
      name: "",
      cell: (row) => (
        <input type="checkbox" checked={compareItems.includes(row.product_id)} onChange={() => toggleCompare(row.product_id)} />
      ),
      width: "40px",
    },
    { name: "Name", selector: (row) => row.product_name, sortable: true, grow: 2 },
    {
      name: "Type",
      cell: (row) => (
        <Badge color={row.item_type === "local" ? "primary" : "warning"} pill>
          {row.item_type}
        </Badge>
      ),
    },
    { name: "Sales", selector: (row) => row.total_sales || 0, sortable: true },
    { name: "Qty", selector: (row) => row.total_quantity || 0, sortable: true },
    { name: "Amount", selector: (row) => getFormattedPrice(row.total_amount || 0), sortable: true },
    { name: "Tax", selector: (row) => getFormattedPrice(row.total_tax || 0), sortable: true },
  ];

  const miniColumns = [
    { name: "Name", selector: (row) => row.product_name, grow: 2 },
    { name: "Qty", selector: (row) => row.total_quantity || 0 },
    { name: "Amount", selector: (row) => getFormattedPrice(row.total_amount || 0) },
  ];

  const localCount = details.filter((i) => i.item_type === "local").length;
  const globalCount = details.filter((i) => i.item_type === "global").length;

  return (
    <div className="page-body">
      <Container className="mt-2">
        <h4 className="text-center mb-3">Sales Report by Items</h4>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input type="text" placeholder="Search item..." value={itemFilter} onChange={(e) => setItemFilter(e.target.value)} />
          <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
          <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
          <Button color="primary" onClick={fetchSalesReport}>Filter</Button>
          <Button color="info" onClick={() => { setItemFilter(""); setDateRange({ start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0], end: new Date().toISOString().split("T")[0] }); fetchSalesReport(); }}>Reset</Button>
        </div>

        {/* Item type tabs */}
        <div className="d-flex gap-2 mb-3">
          {[
            { key: "all", label: `All (${details.length})` },
            { key: "local", label: `Local (${localCount})` },
            { key: "global", label: `Global (${globalCount})` },
          ].map((t) => (
            <Button key={t.key} size="sm" color={itemTypeTab === t.key ? "primary" : "outline-primary"} onClick={() => setItemTypeTab(t.key)}>
              {t.label}
            </Button>
          ))}
        </div>

        <Nav tabs className="mb-3">
          {["overview", "listing", "top & least", "compare"].map((tab) => (
            <NavItem key={tab}>
              <NavLink className={classnames({ active: activeTab === tab })} onClick={() => setActiveTab(tab)} style={{ cursor: "pointer" }}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {loading ? (
          <div className="text-center"><Spinner color="primary" size="lg" /></div>
        ) : (
          <TabContent activeTab={activeTab}>
            {/* Overview */}
            <TabPane tabId="overview">
              <Row className="mb-4">
                {[
                  { label: "Total Items", value: filteredItems.length },
                  { label: "Total Qty", value: s.total_quantity || 0 },
                  { label: "Revenue", value: getFormattedPrice(s.total_amount || 0) },
                  { label: "Tax", value: getFormattedPrice(s.total_tax || 0) },
                ].map((c, i) => (
                  <Col md="3" key={i}>
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <CardTitle tag="h6">{c.label}</CardTitle>
                        <h4>{c.value}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Bar chart of top items */}
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">Top Items by Revenue</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sorted.slice(0, 10).map((i) => ({ name: i.product_name, Revenue: parseFloat(i.total_amount || 0), Qty: i.total_quantity || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip formatter={(v) => typeof v === "number" ? `$${v.toFixed(2)}` : v} />
                    <Legend />
                    <Bar dataKey="Revenue" fill="#28a745" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </TabPane>

            {/* Listing */}
            <TabPane tabId="listing">
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">Items ({filteredItems.length})</h5>
                <DataTable columns={columns} data={filteredItems} pagination striped highlightOnHover />
              </Card>
            </TabPane>

            {/* Top & Least */}
            <TabPane tabId="top & least">
              <Row>
                <Col md="6">
                  <Card className="p-3 shadow-sm mb-3">
                    <h5 className="mb-3 text-success">🔥 Top 5 Sold</h5>
                    <DataTable columns={miniColumns} data={top5} striped noHeader />
                  </Card>
                </Col>
                <Col md="6">
                  <Card className="p-3 shadow-sm mb-3">
                    <h5 className="mb-3 text-danger">📉 Least 5 Sold</h5>
                    <DataTable columns={miniColumns} data={least5} striped noHeader noDataComponent="Not enough items" />
                  </Card>
                </Col>
              </Row>

              {/* Side by side bar */}
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">Top vs Least Comparison</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...top5, ...least5].map((i) => ({
                    name: i.product_name,
                    Quantity: i.total_quantity || 0,
                    Revenue: parseFloat(i.total_amount || 0),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Quantity" fill="#007bff" />
                    <Bar dataKey="Revenue" fill="#28a745" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </TabPane>

            {/* Compare */}
            <TabPane tabId="compare">
              <Card className="p-3 shadow-sm mb-3">
                <h5 className="mb-3">Custom Comparison</h5>
                <p className="text-muted">Select items from the Listing tab using checkboxes, then view comparison here.</p>
                {compareItems.length > 0 && (
                  <div className="mb-3">
                    <strong>Selected ({compareItems.length}):</strong>{" "}
                    {compareItems.map((id) => {
                      const item = details.find((i) => i.product_id === id);
                      return item ? (
                        <Badge key={id} color="info" className="me-1" style={{ cursor: "pointer" }} onClick={() => toggleCompare(id)}>
                          {item.product_name} ×
                        </Badge>
                      ) : null;
                    })}
                    <Button size="sm" color="danger" className="ms-2" onClick={() => setCompareItems([])}>Clear All</Button>
                  </div>
                )}
                {compareChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={compareChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Quantity" fill="#007bff" />
                      <Bar dataKey="Revenue" fill="#28a745" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted">No items selected for comparison</p>
                )}
              </Card>
            </TabPane>
          </TabContent>
        )}
      </Container>
    </div>
  );
};

export default SalesReportByItems;
